import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import {
  getMerchantPaymentAccess,
  setMerchantPaymentMethods,
} from '@/lib/merchantPaymentAccess';

// ─────────────────────────────────────────────────────────────
// « Ma boutique en ligne » — moyens de paiement du commerce.
//
// Le SuperAdmin active les moyens au niveau plateforme (FeexPay, PayPal…)
// et autorise — ou non — un commerce à encaisser. Ici, le commerce voit son
// autorisation et choisit, parmi les moyens autorisés, ceux que SON checkout
// doit proposer. Le serveur revérifie ce choix à chaque paiement.
// ─────────────────────────────────────────────────────────────

interface ProviderRow {
  provider: string;
  label: string;
}

const PROVIDER_ICONS: Record<string, string> = {
  feexpay: 'ri-smartphone-line',
  paypal: 'ri-paypal-line',
  stripe: 'ri-bank-card-line',
  cmi: 'ri-bank-line',
  wave: 'ri-water-flash-line',
  flutterwave: 'ri-flashlight-line',
};

export default function MerchantPaymentMethodsTab({ commerceId }: { commerceId: number }) {
  const [providers, setProviders] = useState<ProviderRow[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [authorized, setAuthorized] = useState(false);
  const [commission, setCommission] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    if (!commerceId) { setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const [providersRes, settingsRes, access] = await Promise.all([
        supabase.from('payment_providers').select('provider, label').eq('enabled', true).order('sort_order'),
        supabase.from('platform_payment_settings').select('scope, commission_percent').eq('id', 1).maybeSingle(),
        getMerchantPaymentAccess(commerceId),
      ]);
      const list = ((providersRes.data || []) as ProviderRow[]).filter((p) => p.provider);
      setProviders(list);
      const scope = (settingsRes.data as { scope?: string } | null)?.scope === 'all' ? 'all' : 'subscriptions';
      setAuthorized(scope === 'all' || access?.enabled === true);
      const globalCommission = Number((settingsRes.data as { commission_percent?: number } | null)?.commission_percent);
      setCommission(access?.commissionPercent ?? (Number.isFinite(globalCommission) ? globalCommission : null));
      // Aucun choix encore enregistré → tous les moyens autorisés sont actifs.
      setSelected(access && access.methods.length > 0 ? access.methods : list.map((p) => p.provider));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger vos moyens de paiement.');
    } finally {
      setLoading(false);
    }
  }, [commerceId]);

  useEffect(() => { void load(); }, [load]);

  const toggle = (provider: string) => {
    setSaved(false);
    setSelected((prev) => (prev.includes(provider) ? prev.filter((p) => p !== provider) : [...prev, provider]));
  };

  const save = async () => {
    if (!authorized || saving) return;
    setSaving(true);
    setError('');
    try {
      const methods = await setMerchantPaymentMethods({
        commerceId,
        methods: selected,
        allowed: providers.map((p) => p.provider),
        enabled: true,
      });
      setSelected(methods);
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Impossible d'enregistrer vos préférences.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <i className="ri-loader-4-line animate-spin text-xl text-foreground-400"></i>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-4">
      <div className={`rounded-lg border p-4 ${authorized ? 'border-accent-200/70 bg-accent-50/50' : 'border-amber-200 bg-amber-50'}`}>
        <div className="flex items-start gap-3">
          <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${authorized ? 'bg-accent-100 text-accent-700' : 'bg-amber-100 text-amber-700'}`}>
            <i className={authorized ? 'ri-checkbox-circle-line' : 'ri-lock-line'}></i>
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground-900">
              {authorized ? 'Paiement en ligne activé sur votre boutique' : "Paiement en ligne pas encore activé"}
            </p>
            <p className="text-xs text-foreground-600 mt-0.5 leading-relaxed">
              {authorized
                ? 'Vos clients peuvent régler directement sur votre page de commande. Le montant net est crédité sur votre portefeuille Zifek.'
                : 'Vos clients commandent normalement et règlent à la livraison. Demandez à l’équipe Zifek (ou au SuperAdmin) d’ouvrir le paiement en ligne pour votre boutique.'}
            </p>
            {commission != null && (
              <p className="text-[11px] text-foreground-500 mt-1.5">
                Commission par transaction : <span className="font-semibold text-foreground-800">{commission} %</span> (frais opérateur inclus).
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-background-200/70">
          <i className="ri-secure-payment-line text-primary-500"></i>
          <h3 className="text-sm font-semibold text-foreground-900">Moyens proposés sur mon checkout</h3>
        </div>
        <div className="p-4 space-y-2">
          {providers.length === 0 && (
            <p className="text-sm text-foreground-500">Aucun moyen de paiement n’est activé par la plateforme pour le moment.</p>
          )}
          {providers.map((p) => {
            const active = selected.includes(p.provider);
            return (
              <button
                key={p.provider}
                type="button"
                disabled={!authorized}
                onClick={() => toggle(p.provider)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border text-left transition-colors ${
                  active ? 'border-primary-300 bg-primary-50/50' : 'border-background-200/70 bg-background-50'
                } ${authorized ? 'cursor-pointer hover:border-primary-200' : 'opacity-60 cursor-not-allowed'}`}
              >
                <span className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${active ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-500'}`}>
                  <i className={PROVIDER_ICONS[p.provider] || 'ri-bank-card-line'}></i>
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium text-foreground-900">{p.label || p.provider}</span>
                  <span className="block text-[11px] text-foreground-500">
                    {p.provider === 'feexpay' ? 'Mobile Money et carte bancaire' : 'Disponible sur votre boutique'}
                  </span>
                </span>
                <span className={`w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 ${active ? 'bg-primary-500 border-primary-500 text-background-50' : 'border-background-300'}`}>
                  {active && <i className="ri-check-line text-[11px]"></i>}
                </span>
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-t border-background-200/70 bg-background-100/40">
          <p className="text-[11px] text-foreground-500 flex-1 min-w-[200px]">
            Seuls les moyens cochés apparaîtront sur la page de commande de votre site.
          </p>
          <button
            type="button"
            onClick={() => { void save(); }}
            disabled={!authorized || saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
          >
            {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
            Enregistrer
          </button>
        </div>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-lg bg-red-50 text-red-600 text-xs flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          {error}
        </div>
      )}
      {saved && (
        <div className="px-4 py-3 rounded-lg bg-accent-50 text-accent-800 text-xs flex items-center gap-2">
          <i className="ri-checkbox-circle-line"></i>
          Vos moyens de paiement ont été enregistrés.
        </div>
      )}
    </div>
  );
}